import { useContext, useEffect, useRef } from 'react';
import { Center, Loader, Stack } from '@mantine/core';
import { Outlet, Navigate, useLocation } from 'react-router-dom';

import routes from 'utils/routes';
import AuthContext from 'context/AuthContext';
import TrackingContext from 'context/TrackingContext';

const ProtectedRoute = () => {
  const location = useLocation();
  const {
    data: { userFetched, registerEmail, newSignup, account },
    actions: { isLoggedIn },
  } = useContext(AuthContext);
  const {
    actions: { trackSignUpComplete },
  } = useContext(TrackingContext);

  // Ref to ensure we only send the sign_up_completed event once (avoids React 18 StrictMode double effect execution)
  const signUpTrackedRef = useRef(false);

  useEffect(() => {
    if (!signUpTrackedRef.current && newSignup && account?.id) {
      trackSignUpComplete(account.id.toString());
      signUpTrackedRef.current = true;
    }
  }, [newSignup, account?.id, trackSignUpComplete]);

  if (!userFetched) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Loader size="md" />
        </Stack>
      </Center>
    );
  }

  const isAuthenticated = isLoggedIn();
  const isRegistrationFlow = !!registerEmail;
  const isWelcome = location.pathname === routes.welcomePath.href;
  const isOnboarding = location.pathname === routes.onboardingPath.href;

  if ((isRegistrationFlow && isOnboarding) || (newSignup && isWelcome)) {
    return <Outlet />;
  }

  if (newSignup) {
    return <Navigate to={routes.welcomePath.href} replace />;
  }

  if (isRegistrationFlow && isOnboarding) {
    return <Outlet />;
  }

  if (isRegistrationFlow && !isOnboarding) {
    return <Navigate to={routes.onboardingPath.href} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={routes.signInPath.href} replace />;
  }

  if (isAuthenticated && (isOnboarding || isWelcome)) {
    return <Navigate to={routes.dashboardPath.href} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
